import { Chip, Stack, useMediaQuery } from '@mui/material'
import { useNavigate, useParams, } from 'react-router-dom'
import gaiaTheme from '../../../../root-components/theme';
import { useContext, useEffect, useState } from 'react';
import EmailCampaignService from '../../../../modules/survey/shared-services/email-campaign/email-campaign.service';
import { IEmailCampaign } from '../../../../modules/survey/shared-services/email-campaign/email-campaign.model';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import moment from 'moment';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { useHasOpenReportPeriod } from '../../../../hooks/use-has-open-report-period';
import DistributionListService from '../../../../shared/services/distribution-list/distribution-list.service';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';

//The legacy product data-request branch of this page was retired when supplier product data
//requests moved onto the survey engine (Surveys, type "Supplier product data request"); only
//the legacy employee survey listing remains here.

export const EmissionsSurveyTablePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  //Services
  const emailCampaignService = new EmailCampaignService();
  const distributionListService = new DistributionListService();
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  //State
  const [hasDistributionLists, setHasDistributionLists] = useState<boolean>(false)
  const [emailCampaigns, setEmailCampaigns] = useState<IEmailCampaign[]>()
  const [dialogOpen, setDialogOpen] = useState(false);
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  const [selected, setSelected] = useState<string[]>([]);
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  //Hooks
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedReportId,
  ]);
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Use Effects
  useEffect(() => {
    if (id === 'employee' && reportPeriodContext.lastSelectedReportId) {
      emailCampaignService.getAllEmailCampaignsWithPagination(reportPeriodContext.lastSelectedReportId, 'EMPLOYEE_COMMUTE_EMAIL', inputPage, perPage, orderBy, order)
        .then(response => {
          setIsLoading(false)
          setEmailCampaigns(response.rows)
          setTotalRecords(response.count);
        })
        .catch(err => {
          setIsLoading(false)
          console.log(err)
        })
    } else {
      setIsLoading(false)
    }
  }, [reportPeriodContext.lastSelectedReportId, inputPage, perPage, change])

  useEffect(() => {
    distributionListService.getAll()
      .then(response => {
        setHasDistributionLists(response.length > 0)
      })
      .catch(err => {
        console.log(err)
      })
  }, [])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage !== 0) {
        setInputPage(0)
      }
      setChange(change + 1)
    }
    setHasPageMounted(true)
  }, [reportPeriodContext.lastSelectedReportId, orderBy, order])


  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/emissions/surveys/employee/overview?campaignId=${selectedRowId[0]}`)
    }
  }

  const headCellsEmployee: IHeadCell[] = [
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'createdAt', numeric: false, label: 'Created On', sortable: true },
    { id: 'reportPeriod', numeric: false, label: 'Report Period', sortable: true },
    { id: 'active', numeric: false, label: 'Active', sortable: true, isSubHeading: true },
    { id: 'response', numeric: false, label: 'Responses', sortable: false },
  ];


  const rowsEmployee = !emailCampaigns ? [] : emailCampaigns.map(campaign => {

    return {
      id: campaign.id,
      name: campaign.name,
      createdAt: moment(campaign.createdAt).format('DD/MM/YYYY HH:mm'),
      reportPeriod: reportPeriodContext.allReportPeriods.find(reportPeriod => reportPeriod.id === campaign.reportPeriodId)?.friendlyName,
      active: campaign.active ? 'Yes' : 'No',
      response: (<Chip variant='outlined' sx={{ pl: 1, pr: 1 }} color={campaign.completedCount === 0 ? 'error' : 'primary'} label={campaign.completedCount} />)
    }
  })

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  return (
    <PageOuterContainer>
      {id && <PageHeading
        title={`${id.charAt(0).toUpperCase() + id.slice(1).toLowerCase()} Surveys `}
        includeBackButton={true}
        backButtonLink='/emissions/surveys'
        backButtonText='Back to surveys'
        hasTabs={false}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate(`/emissions/surveys/${id}/add-new-survey`)}
        actionButtonText='Create Survey'
        hideShowActionButton={!hasDistributionLists || !hasOpenReport || isReportPeriodLocked === true}
      />}
      {hasOpenReport !== undefined && hasOpenReport === false &&
        <>
          <ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
          {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
        </>
      }
      <LoadingPage pageReady={!isLoading}>
        {emailCampaigns && id === 'employee' &&
          <>
            {!hasDistributionLists &&
              <Stack sx={{ mb: 2 }}>
                <InformationBox
                  icon={IconName.ErrorOutline}
                  text={'You do not have any distribution lists'}
                  actionText='Click here to create one'
                  link='/settings/company-settings/distribution-lists?openDialog=true'
                />
              </Stack>}
            <TableDisplay
              isMobileMode={isMobileMode}
              headCells={headCellsEmployee}
              rows={rowsEmployee}
              onOrderChange={handleOrderChange}
              selected={selected}
              setSelected={setSelected}
              handleSelected={handleSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          </>}
      </LoadingPage>
    </PageOuterContainer>
  )
}
